/**
 * @file DialogManageSessions Component
 * @description This component renders a dialog for managing user sessions across different devices.
 * It displays a list of active and previously active devices, allowing users to log out from individual devices or all devices at once.
 */

import {
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import DialogWrapper from './dialog-wrapper';
import { getColor } from '../../assets/theme';
import {
  DesktopWindowsOutlined,
  DevicesOutlined,
  Logout,
  PhoneIphoneOutlined,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import useConfirmationDialog from '../../hooks/useConfirmationDialog';
import {
  useDeleteDevicesMutation,
  useGetDevicesQuery,
} from '@api/users-management/user-mangement.api.rtk';
import useUser from '@hooks/useUser';
import useAppContext from '@hooks/useAppContext';
/**
 * @interface DialogManageSessions
 * @property {boolean} open - Determines whether the dialog is open or closed
 * @property {() => void} onClose - Function to call when the dialog should be closed
 */
interface DialogManageSessions {
  open: boolean;
  onClose: () => void;
}

interface IDeviceInfo {
  type: string;
  model: string;
  vendor: string;
}

interface IApiDevice {
  operating_system: string;
  os_version: string;
  browser: string;
  browser_version: string;
  ip_address: string;
  mac_address: string;
  device: IDeviceInfo;
  verified: number;
  active: number;
}

interface IDeviceResponse {
  data: {
    devices: IApiDevice[];
  };
}

interface IDevice {
  title: string;
  description: string;
  device: string;
  active: boolean;
}
/**
 * DialogManageSessions component
 * @param {DialogManageSessions} props - Component props
 * @returns {JSX.Element} Rendered component
 */
const DialogManageSessions = ({ open, onClose }: DialogManageSessions) => {
  const userData = useUser();
  const { toast } = useAppContext();

  const { white, primary, textSecondary, borderColor } = getColor();

  const { data, isLoading, refetch } = useGetDevicesQuery(userData?.userID, {
    skip: !open || !userData?.userID,
  });

  const [deleteDevices, { isLoading: isDeleting }] = useDeleteDevicesMutation();

  const { DialogComponentV2, onShowDialog, onCloseDialog, showDialog } =
    useConfirmationDialog();

  const getDeviceType = (item: IApiDevice) => {
    const os = item.operating_system?.toLowerCase() || '';

    if (
      os.includes('android') ||
      os.includes('ios') ||
      os.includes('iphone') ||
      os.includes('ipad')
    ) {
      return 'phone';
    }

    return 'desktop';
  };

  const devices: IDevice[] =
    (data as IDeviceResponse)?.data?.devices?.map(
      (item): IDevice => ({
        title:
          [item.device?.vendor, item.device?.model].filter(Boolean).join(' ') ||
          item.browser ||
          'Unknown Device',

        description: `${item.operating_system || 'Unknown OS'} ${
          item.os_version || ''
        } • ${item.browser || 'Unknown Browser'} ${item.browser_version || ''}`,

        device: getDeviceType(item),

        active: item.active === 1,
      }),
    ) ?? [];

  const handleDeleteDevices = async () => {
    try {
      await deleteDevices(userData?.userID).unwrap();

      await refetch();

      toast({
        severity: 'success',
        message: 'Successfully removed devices',
      });

      onCloseDialog();
    } catch (error) {
      console.error('Failed to delete devices:', error);

      toast({
        severity: 'error',
        message: 'Unable to remove devices',
      });
    }
  };

  const activeDevices = devices.filter((item) => item.active);

  const previousDevices = devices.filter((item) => !item.active);

  const renderDeviceIcon = (device: string) => {
    switch (device) {
      case 'phone':
        return (
          <PhoneIphoneOutlined
            sx={{
              color: primary,
            }}
          />
        );

      case 'desktop':
      default:
        return (
          <DesktopWindowsOutlined
            sx={{
              color: primary,
            }}
          />
        );
    }
  };

  const renderDevice = (item: IDevice, active = false, index: number) => (
    <ListItem
      key={index}
      sx={{
        borderTop: `1px solid ${borderColor}`,

        ...(active && {
          borderBottom: `1px solid ${borderColor}`,
        }),

        gap: '16px',
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 'auto',
        }}
      >
        {renderDeviceIcon(item.device)}
      </ListItemIcon>

      <ListItemText>
        <Stack direction='row' alignItems='center' gap='16px'>
          <Stack>
            <Typography mb={0.8}>
              {item.title}

              {active && (
                <Chip
                  label='Active Now'
                  sx={{
                    color: white,
                    bgcolor: primary,
                    ml: '8px',
                    height: '30px',
                    fontSize: '10px',
                  }}
                />
              )}
            </Typography>

            <Typography
              sx={{
                color: textSecondary,
              }}
            >
              {item.description}
            </Typography>
          </Stack>

          <IconButton disableRipple>
            <Logout />
          </IconButton>
        </Stack>
      </ListItemText>
    </ListItem>
  );

  return (
    <>
      <DialogWrapper open={open} onClose={onClose}>
        <Box>
          <Stack
            sx={{
              bgcolor: white,
              borderRadius: '4px',
              p: '24px',
            }}
          >
            <DevicesOutlined
              sx={{
                width: '59px',
                height: '59px',
                bgcolor: '#F7F7FB',
                p: '12px',
                borderRadius: '100%',
                color: primary,
              }}
            />

            <DialogTitle
              sx={{
                p: '8px 0',
                fontWeight: 600,
              }}
            >
              Your Devices
            </DialogTitle>

            <DialogContent
              sx={{
                p: '16px 0!important',
              }}
            >
              <Stack gap='16px' mb='16px'>
                {isLoading ? (
                  <Typography>Loading devices...</Typography>
                ) : (
                  activeDevices.map((item, index) =>
                    renderDevice(item, true, index),
                  )
                )}

                <Typography
                  sx={{
                    color: textSecondary,
                  }}
                >
                  Your devices linked to this account
                </Typography>

                <LoadingButton
                  variant='contained'
                  loading={isDeleting}
                  onClick={onShowDialog}
                >
                  Remove all other verified devices
                </LoadingButton>
              </Stack>

              <List>
                {previousDevices.map((item, index) =>
                  renderDevice(item, false, index),
                )}
              </List>
            </DialogContent>

            <DialogActions
              sx={{
                p: '0 0 8px 0',
              }}
            >
              <Button variant='outlined' onClick={onClose}>
                Cancel
              </Button>
            </DialogActions>
          </Stack>
        </Box>
      </DialogWrapper>

      <DialogComponentV2
        open={showDialog}
        onConfirm={handleDeleteDevices}
        onClose={onCloseDialog}
      />
    </>
  );
};

export default DialogManageSessions;
